import React, { MouseEvent, PropsWithChildren, useState } from "react";
import * as RadixTooltip from "@opstel/radix/Tooltip";
import styled from "styled-components";
import { palette } from "@shared/EDS/ns-color-palette";
import {
  TOOLTIP_DELAY,
  TOOLTIP_OFFSET_BETWEEN_TRIGGER_AND_ARROW,
} from "@opstel/SBD/core/layoutConstants";

interface TooltipProps {
  readonly displayAsChild?: boolean;
  readonly open?: boolean;
  readonly xPosition?: "mouse" | "center";
  readonly enabled?: boolean;
}

export const StyledTooltipContent = styled(RadixTooltip.Content)`
  color: white;
  background-color: ${palette.gray700};
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing.spacing2.toString()}
    ${({ theme }) => theme.spacing.spacing3.toString()};
  box-shadow: 0 4px 24px 0 #0000001f;
  z-index: 200;
  ${({ theme }) => theme.typography.text.regular.sm};
`;

export const StyledTooltipArrow = styled(RadixTooltip.Arrow)`
  fill: ${palette.gray700};
  width: 16px;
  height: 8px;
  top: -6.5px;
`;

function mouseOffsetXRelativeToTarget(event: MouseEvent): number {
  const boundingRect = (event.target as HTMLElement).getBoundingClientRect();
  return event.clientX - boundingRect.left;
}

export function Tooltip({
  children,
  displayAsChild = false,
  open,
  xPosition = "mouse",
  enabled = true,
}: PropsWithChildren<TooltipProps>): React.JSX.Element {
  const [tooltipTrigger, tooltipContent] = React.Children.toArray(children);
  const [mouseX, setMouseX] = useState(0);
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);

  const handleMouseEnterTooltip = () => setIsTooltipHovered(true);
  const handleMouseLeaveTooltip = () => setIsTooltipHovered(false);

  const determineContentPosition = (event: MouseEvent) => {
    if (isTooltipHovered) return;
    setMouseX(mouseOffsetXRelativeToTarget(event));
  };

  return enabled ? (
    <div>
      <RadixTooltip.Provider delayDuration={TOOLTIP_DELAY}>
        <RadixTooltip.Root open={open}>
          <Trigger asChild={displayAsChild} onMouseEnter={determineContentPosition}>
            {tooltipTrigger}
          </Trigger>
          <RadixTooltip.Portal>
            <StyledTooltipContent
              sideOffset={TOOLTIP_OFFSET_BETWEEN_TRIGGER_AND_ARROW}
              side="bottom"
              align={xPosition === "mouse" ? "start" : "center"}
              alignOffset={xPosition === "mouse" ? mouseX : 0}
              avoidCollisions={true}
              onMouseEnter={handleMouseEnterTooltip}
              onMouseLeave={handleMouseLeaveTooltip}
            >
              {tooltipContent}
              <StyledTooltipArrow />
            </StyledTooltipContent>
          </RadixTooltip.Portal>
        </RadixTooltip.Root>
      </RadixTooltip.Provider>
    </div>
  ) : (
    (tooltipTrigger as React.JSX.Element)
  );
}

const Trigger = styled(RadixTooltip.Trigger)`
  border: 0;
  background: none;
`;
